import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { DotsSixVertical } from "phosphor-react";
import { Reorder } from "framer-motion";
import fieldTemplatesStyles from "../Settings/FieldTemplatesSettings/FieldTemplatesStyles";
import { fget } from "../../API/callsAPI";

export default function CustomPageColumnsEdit({
  trackType = "asset",
  onSelectColumns,
  projectId,
  selectedTrackable,
  setSelectedTrackable,
}) {
  const classes = fieldTemplatesStyles();
  const [availableColumns, setAvailableColumns] = useState([]);
  const [customColumns, setCustomColumns] = useState([]);
  const [selectedDefaultNames, setSelectedDefaultNames] = useState(new Set());
  const [selectedCustomIdentifiers, setSelectedCustomIdentifiers] = useState(
    new Set()
  );
  const [orderedSelectedColumns, setOrderedSelectedColumns] = useState([]);
  const [trackableOptions, setTrackableOptions] = useState([]);

  const selectedColumns = useMemo(() => {
    if (orderedSelectedColumns.length > 0) {
      return orderedSelectedColumns;
    }
    const selectedDefault = availableColumns.filter((col) =>
      selectedDefaultNames.has(col.name)
    );
    const selectedCustom = customColumns.filter((col) =>
      selectedCustomIdentifiers.has(col.identifier)
    );
    return [...selectedDefault, ...selectedCustom];
  }, [
    availableColumns,
    customColumns,
    selectedDefaultNames,
    selectedCustomIdentifiers,
    orderedSelectedColumns,
  ]);

  useEffect(() => {
    // Fetch default and custom fields based on trackable type received, none if  selected "manual table"
    if (trackType === "manual_table") {
      setAvailableColumns([]);
      setTrackableOptions([]);
    } else if (trackType === "assets") {
      setTrackableOptions([
        { id: 12, name: "Asset" },
        { id: 18, name: "Asset Version" },
      ]);
      setSelectedTrackable({ id: 12, name: "Asset" });
    } else if (trackType === "sequences") {
      setTrackableOptions([
        { id: 13, name: "Sequence" },
        { id: 14, name: "shot" },
        { id: 17, name: "shot version" },
      ]);
      setSelectedTrackable({ id: 13, name: "Sequence" });
    } else if (trackType === "tasks") {
      setTrackableOptions([{ id: 10, name: "Task" }]);
      setSelectedTrackable({ id: 10, name: "Task" });
    }
  }, [trackType]);

  useEffect(() => {
    if (selectedTrackable.id) {
      handleFetchAvailableFields(selectedTrackable.id);
    }
  }, [selectedTrackable]);

  const handleFetchAvailableFields = async (trackableId) => {
    try {
      console.log("Fetching available fields for trackable:", trackableId);
      const res = await fget({
        url: `trackables/trackable-field/?project=${projectId}&trackable=${trackableId}`,
      });
      console.log(res);

      const defaultFields = res?.data?.default_fields.map((field) => ({
        id: field.id,
        name: field.name,
        isRequired: field.required,
      }));

      setAvailableColumns(defaultFields);

      setCustomColumns(
        res.data.custom_fields.map((field) => ({
          id: field.id,
          name: field.name,
          identifier: field.identifier,
          isRequired: false,
        }))
      );

      const requiredFields = defaultFields.filter((field) => field.isRequired);
      const requiredFieldNames = new Set(
        requiredFields.map((field) => field.name)
      );

      setSelectedDefaultNames(requiredFieldNames);
      setSelectedCustomIdentifiers(new Set());
      setOrderedSelectedColumns(requiredFields);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDefaultColumnToggle = useCallback(
    (name) => {
      console.log("clicked on: ", name);
      setSelectedDefaultNames((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(name)) {
          newSet.delete(name);
        } else {
          newSet.add(name);
        }
        return newSet;
      });

      setOrderedSelectedColumns((prev) => {
        const column = availableColumns.find((col) => col.name === name);
        if (!column) return prev;

        const exists = prev.some((col) => col.name === name);
        if (exists) {
          return prev.filter((col) => col.name !== name);
        } else {
          return [...prev, column];
        }
      });
    },
    [availableColumns]
  );

  const handleCustomColumnToggle = useCallback(
    (identifier) => {
      setSelectedCustomIdentifiers((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(identifier)) {
          newSet.delete(identifier);
        } else {
          newSet.add(identifier);
        }
        return newSet;
      });

      setOrderedSelectedColumns((prev) => {
        const column = customColumns.find(
          (col) => col.identifier === identifier
        );
        if (!column) return prev;

        const exists = prev.some((col) => col.identifier === identifier);
        if (exists) {
          return prev.filter((col) => col.identifier !== identifier);
        } else {
          return [...prev, column];
        }
      });
    },
    [customColumns]
  );

  const handleReorder = useCallback((newOrder) => {
    setOrderedSelectedColumns(newOrder);
  }, []);

  useEffect(() => {
    onSelectColumns(orderedSelectedColumns);
    return () => {};
  }, [orderedSelectedColumns]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Box>
        <Typography
          sx={{ mb: 1, fontSize: "14px", color: "rgba(255,255,255,0.7)" }}
        >
          Which row type fields this table?*
        </Typography>
        <Box className={classes.cardContentDiv3}>
          <Select
            value={selectedTrackable.id || ""}
            onChange={(e) => {
              const trackValue = {
                id: parseInt(e.target.value),
                name: trackableOptions.find(
                  (option) => option.id === parseInt(e.target.value)
                ).name,
              };
              setSelectedTrackable(trackValue);
            }}
            fullWidth
            sx={{
              color: "white",
              fontSize: "16px",
              fontFamily: "poppins",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiSelect-select": {
                padding: "6px",
              },
              "& .MuiSvgIcon-root": {
                color: "white",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "#252A38",
                  color: "white",
                  "& .MuiMenuItem-root": {
                    "&:hover": {
                      backgroundColor: "#2D3240",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#6C65D9",
                      "&:hover": {
                        backgroundColor: "#5A55C7",
                      },
                    },
                  },
                },
              },
            }}
          >
            {trackableOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{ mb: 2, fontSize: "14px", color: "rgba(255,255,255,0.7)" }}
        >
          Available columns from this row type:
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            gap: "12px",
          }}
        >
          {availableColumns.map((column) => (
            <FormControlLabel
              key={`default-${column.name}`}
              control={
                <Checkbox
                  checked={selectedDefaultNames.has(column.name)}
                  onChange={() => handleDefaultColumnToggle(column.name)}
                  sx={{
                    color: "#606479",
                    "&.Mui-checked": {
                      color: "#6C65D9",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Typography sx={{ fontSize: "14px", color: "white" }}>
                    {column.name}
                  </Typography>
                  {column.isRequired && (
                    <Typography sx={{ fontSize: "14px", color: "#FF6B6B" }}>
                      *
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                backgroundColor: "#252A38",
                borderRadius: "8px",
                padding: "8px 12px",
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  width: "100%",
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{ mb: 2, fontSize: "14px", color: "rgba(255,255,255,0.7)" }}
        >
          Custom columns:
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            gap: "12px",
          }}
        >
          {customColumns.map((column) => (
            <FormControlLabel
              key={`custom-${column.identifier}`}
              control={
                <Checkbox
                  checked={selectedCustomIdentifiers.has(column.identifier)}
                  onChange={() => handleCustomColumnToggle(column.identifier)}
                  sx={{
                    color: "#606479",
                    "&.Mui-checked": {
                      color: "#6C65D9",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "14px", color: "white" }}>
                  {column.name}
                </Typography>
              }
              sx={{
                backgroundColor: "#252A38",
                borderRadius: "8px",
                padding: "8px 12px",
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  width: "100%",
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{ mb: 2, fontSize: "14px", color: "rgba(255,255,255,0.7)" }}
        >
          Table preview:
        </Typography>
        {selectedColumns.length > 0 ? (
          <Box
            sx={{
              overflowX: "auto",
              overflowY: "visible",
              pb: 1,
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#606479",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#707489",
                },
              },
            }}
          >
            <Reorder.Group
              axis="x"
              values={selectedColumns}
              onReorder={handleReorder}
              role="list"
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                paddingLeft: "0px",
                listStyle: "none",
                width: "max-content",
                minHeight: "60px",
              }}
            >
              {selectedColumns.map((column, index) => {
                const isCustomColumn = customColumns.some(
                  (cc) => cc.identifier === column.identifier
                );
                const uniqueKey = isCustomColumn
                  ? `custom-${column.identifier}`
                  : `default-${column.name}`;

                return (
                  <Reorder.Item
                    drag
                    key={uniqueKey}
                    value={column}
                    role="listitem"
                    style={{
                      cursor: "grab",
                      flexShrink: 0,
                      minWidth: "fit-content",
                    }}
                    whileDrag={{
                      scale: 1.05,
                      zIndex: 1000,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#252A38",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        minWidth: "120px",
                        border: "1px solid transparent",
                        "&:active": {
                          cursor: "grabbing",
                        },
                      }}
                    >
                      <DotsSixVertical
                        size={16}
                        color="#606479"
                        style={{ cursor: "grab" }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "white",
                            fontWeight: 500,
                          }}
                        >
                          {column.name}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#606479" }}>
                          {isCustomColumn ? "Custom Field" : "Default Field"}
                        </Typography>
                      </Box>
                    </Box>
                  </Reorder.Item>
                );
              })}
            </Reorder.Group>
          </Box>
        ) : (
          <Typography sx={{ color: "#606479", fontSize: "14px" }}>
            Select columns to add them to preview
          </Typography>
        )}
      </Box>
    </Box>
  );
}
